import React, { useEffect, useState } from 'react'
import useSiteData from '../../useSiteData'
import App from '../App'
import Head from '../Head'
import Hero from '../organisms/Hero'
import { headerThemes, PagesContext, HeaderThemeContext } from '../../context'
import { getLabelForDuration, markdownToJSX } from '../../util/transforms'
import ContentSelector from '../organisms/ContentSelector'
import Booking from '../organisms/Booking'
import {
  getAvailableBookingTypes,
  getAvailableDurations,
  getAvailableMandatoryAdditions
} from '../../api/travelize'

// so we can get the currency using same approach as in travelie.js
import { getCurrentLocale } from "../../api/index"
import { getLocaleInfo } from "../../util/contentModelToData"

export default ({ pageContext, children }) => {

  const routeData = pageContext
  const { 
    slug, 
    slugs, 
    subPages,
    subPagesSpecial, 
    travelizeProductCode, 
    travelizeAdditionId, 
    subPage: subPageOriginal, 
    subPageIndex: subPageIndexOriginal, 
    heroImage, 
    heroVideo, 
    heroLabel, 
    heroTitle, 
    overviewText, 
    metaDataTitle, 
    metaDataDescription, 
    metaDataImage, 
    canonicalSlug, 
    topBannerActive, 
    topBannerText, 
    pagePath,
    hideInContentSelector,
    metaDataRobots,
    campNotAvailable
  } = routeData

  const { translations } = useSiteData().pageSettings
  const [packages, setPackages] = useState(null)
  const [locationName, setLocationName] = useState(null)
  const [locationMeta, setLocationMeta] = useState(null)
  const [bookingsAvailable, setBookingsAvailable] = useState(true)
  const [locationPriceFrom, setLocationPriceFrom] = useState(null)
  const locale = getCurrentLocale()
  const { currency } = getLocaleInfo(locale)
  const [canonical, setCanonical] = useState(null)
  const theme = (heroImage || heroVideo) ? headerThemes.LIGHT : headerThemes.DARK


  let subPage = subPageOriginal
  let subPageIndex = subPageIndexOriginal

  const banner = {
      active: topBannerActive || false,
      text: topBannerText || ""
  }

  // make subpage slugs contain parent slug path
  let subPageSlugs = {}
  if (subPage && subPage.slugs) {
    Object.keys(subPage.slugs).forEach(localeKey => {
      subPageSlugs[localeKey] = slugs[localeKey] + '/' + subPage.slugs[localeKey]
    })
  }

  useEffect(() => {
    let slug = null
    if (subPage && subPage.canonicalSlug) {
      slug = subPage.canonicalSlug
    } else if (canonicalSlug) {
      slug = canonicalSlug
    }
    setCanonical(slug)  
  }, [canonicalSlug, subPage])

  useEffect(() => {
    if (travelizeProductCode) {
      getAvailableBookingTypes(travelizeProductCode.toUpperCase()).then((availableBookingTypes) => {
        if (availableBookingTypes.length) {
          const durationItems = getAvailableDurations(availableBookingTypes).sort((a, b) => a - b).map(days => ({
            value: days,
            name: getLabelForDuration(days, translations)
          }))

          const shortestDuration = durationItems[0]

          const bookingDescription = getAvailableMandatoryAdditions(availableBookingTypes, shortestDuration)
          .find(({ additionID }) => {
            return !travelizeAdditionId || additionID === travelizeAdditionId
          })


          const endDate = new Date(availableBookingTypes[0].departureDate)
          endDate.setMonth(endDate.getMonth() + 1)

          setLocationName(bookingDescription.productName)
          setLocationMeta(shortestDuration.name)
          setLocationPriceFrom(bookingDescription.priceFrom)
          setBookingsAvailable(true)
        } else {
          setBookingsAvailable(false)
        }
      })
    } else {
      setBookingsAvailable(false)
    }
  }, [travelizeProductCode])
  
  return (
    <HeaderThemeContext.Provider value={{ theme }}>
      <App banner={ banner }>
        <PagesContext.Provider value={ { slug, subPages, subPagesSpecial } }>
          { subPage && 
            <Head 
              title={ subPage.metaDataTitle || ( (subPage.title && subPage.title.replace(/\*/g, '')) + ' - Lapoint Surf camps' ) }
              description={ subPage.metaDataDescription } 
              image={ subPage.metaDataImage || metaDataImage } 
              canonical={ canonical } 
              slugs={ subPageSlugs } 
              pagePath={ pagePath }
              robots={ subPage.metaDataRobots }
            /> 
          }
          { !subPage && 
            <Head 
              title={ metaDataTitle || (heroLabel + ' - ' + heroTitle + ' - Lapoint Surf camps') }
              description={ metaDataDescription } 
              image={ metaDataImage } 
              canonical={ canonical } 
              slugs={ slugs } 
              pagePath={ pagePath }
              robots={ metaDataRobots }
            /> 
          }
          { /* product_code is added as a attribute so that we easily can grab it from PricingGrid.js */ }
          <main product_code={ bookingsAvailable && travelizeProductCode ? travelizeProductCode : undefined }>
            { (heroImage || heroVideo || heroLabel || heroTitle) &&
              <Hero 
                heroAsH1={ !subPage } 
                image={ heroImage } 
                video={ heroVideo } 
                label={ heroLabel } 
                title={ heroTitle }
                classes="u-section-spacing-bottom u-section-spacing-bottom--narrow"
              />
            }
            <Booking 
              locationName={ locationName }
              locationMeta={ locationMeta }
              locationPriceFrom={ locationPriceFrom }
              currency={ currency }
              productCode={ travelizeProductCode }
              bookingsAvailable={ bookingsAvailable }
              campNotAvailable={ campNotAvailable }
            />
            { (subPages && subPages.length > 0 ) &&
              <ContentSelector
                overviewText={ overviewText }
                parentSlug={ slug }
                productCode={ bookingsAvailable && travelizeProductCode }
                subPages={ subPages }
                selectedIndex={ subPageIndex || 0 }
                hideInContentSelector={ hideInContentSelector ? true : false }
              />
            }

            {children}
          </main>
        </PagesContext.Provider>
      </App>
    </HeaderThemeContext.Provider>
  )
};
